import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alignment, Intent } from '@blueprintjs/core';
import classNames from 'classnames';
import moment from 'moment';

import { Breadcrumbs } from 'components/navigation';
import { Button } from 'components/buttons';
import { FormDateRangeInput, FormSelect, FormSwitch, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';

import { HelperFunctions } from 'helpers';
import { useDebounce } from 'hooks/useDebounce';
import { useRestrictedAccountIds } from 'hooks/useRestrictedAccountIds';
import { RulesEngineService } from 'services';
import { SetRulesEngineDiffViewerReturn, SetRulesEngineDriver, SetRulesEngineRange } from 'state/actions';
import buildXlsxWorkbook from 'utils/buildXlsxWorkbook';

import { isDateRangeValid } from 'pages/internal-admin/rules-engine';
import { DiffViewerComparisonControl } from './DiffViewerComparisonControl';
import { mapControlResponse } from './diffViewerControl';
import { DiffViewerDifferences } from './DiffViewerDifferences';
import { DiffViewerGatePanel } from './DiffViewerGatePanel';
import { DiffViewerGroups } from './DiffViewerGroups';
import { DiffViewerSignOffModal } from './DiffViewerSignOffModal';
import { DiffViewerTrend } from './DiffViewerTrend';
import { buildDiffViewerFileName, buildDiffViewerJson, buildDiffViewerSheets } from './diffViewerDownloads';
import { formatMode, formatValue } from './diffViewerFormatting';
import { buildDiffFilters, buildDiffViewerSearchParams, comparisonModes, labelFilters, parseDiffViewerQuery, positiveIntegerPattern } from './diffViewerQuery';
import { mapCoverageResponse, mapDailyCountsResponse, mapDiffViewerSummary, mapDifferencesResponse, mapGroupsResponse } from './diffViewerResponses';
import { buildTrendPeriod, maxTrendDays, trendPeriodProblems } from './diffViewerTrendPeriod';
import './RulesEngineDiffViewer.css';

const requiredAction = "Internal";
const harnessRoute = "/internal-admin/rules-engine/harness";
const isoDateFormat = "YYYY-MM-DD";
const badRequestCode = 400;
const allOption = "all";
const driverDebounceTimeout = 750;

const differencesErrorMessage = "Could not load the differences. Please try again.";
const groupsErrorMessage = "Could not load the difference groups. Please try again.";
const downloadErrorMessage = "Could not build the download. Please try again.";
const utcNotice = "Times marked (UTC) are UTC and are not converted to UK local time.";
const invalidRangeMessage = "The end date cannot be before the start date.";
const invalidDriverMessage = "Enter the driver's employee id as a number.";
const signedOffMessage = "The difference has been signed off.";
const signOffErrorMessage = "Could not sign off the difference. Please try again.";
const groupSignOffErrorMessage = "Could not sign off the group. Please try again.";
const trendErrorMessage = "Could not load the daily trend. Please try again.";
const excelWarningMessage = "This file contains driver names and card numbers. Treat it as sensitive and permanently delete it when you have finished with it.";
// UnderTest never measures drivers; Legacy v RE2 did, but the comparison queue no longer holds that day.
const notMeasuredText = "not measured";
const notAvailableText = "not available";

const trendUnavailableMessages = {
    [trendPeriodProblems.tooLong]: `The trend covers at most ${maxTrendDays} days. Choose a shorter period to see it.`,
    [trendPeriodProblems.startsAfterToday]: "The chosen period starts after today, so there is no trend to show yet."
};

const introTexts = {
    [comparisonModes.legacyVersusShadow]: "Every difference the comparison recorded between the legacy engine and RE2, grouped by what they have in common. Open a group to see its differences, and open a difference in the harness to rerun its driver.",
    [comparisonModes.underTest]: "Every difference the comparison recorded between the production engine and the RE2 candidate under test, grouped by what they have in common. Open a group to see its differences, and open a difference in the harness to rerun its driver."
};

// Gives the skeleton its size until the comparison shown is known.
const introPlaceholder = introTexts[comparisonModes.legacyVersusShadow];

const labelItems = [
    { id: allOption, name: "All labels" },
    { id: labelFilters.untriaged, name: "Untriaged" },
    { id: labelFilters.knownDifference, name: "Known difference" },
    { id: labelFilters.regression, name: "Regression" }
];

const signOffLabels = [labelFilters.knownDifference, labelFilters.regression];

const defaultArray = [];

// The harness reruns the window the comparison last evaluated. Without one it opens on the UK week the
// difference falls in, with a week either side, and never past today because the harness cannot run ahead.
function buildHarnessRange(difference) {
    const today = moment();
    let start;
    let end;

    if (difference.lastEvaluatedWindowStart != null && difference.lastEvaluatedRunDate != null) {
        start = moment.utc(difference.lastEvaluatedWindowStart);
        end = moment.utc(difference.lastEvaluatedRunDate);
    } else {
        const occurredLocal = moment.utc(difference.occurredAt).local();

        start = occurredLocal.clone().startOf('isoWeek').subtract(1, 'weeks');
        end = occurredLocal.clone().endOf('isoWeek').add(1, 'weeks');
    }

    if (end.isAfter(today)) {
        end = today;
    }

    return {
        startDate: start.format(isoDateFormat),
        endDate: end.format(isoDateFormat)
    };
}

function showRequestError(error, message) {
    if (error?.status === badRequestCode) {
        NotificationToaster.show(Intent.WARNING, error.data);
        return;
    }

    NotificationToaster.show(Intent.DANGER, message);
}

function formatDifferenceCount(count) {
    return `${count} ${count === 1 ? "difference" : "differences"}`;
}

// The endpoint signs off what is open when it runs, which can differ from the count the operator confirmed.
function showGroupSignedOff(signedOffCount, expectedCount) {
    if (signedOffCount === expectedCount) {
        NotificationToaster.show(Intent.SUCCESS, `${formatDifferenceCount(signedOffCount)} signed off.`);
        return;
    }

    const verb = signedOffCount === 1 ? "was" : "were";

    NotificationToaster.show(
        Intent.WARNING,
        `${formatDifferenceCount(signedOffCount)} ${verb} signed off, not the ${expectedCount} expected. The list has been refreshed.`
    );
}

export function RulesEngineDiffViewer({ breadcrumbs = defaultArray }) {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const diffViewerReturn = useSelector((state) => state.rulesEngine.diffViewerReturn);

    const query = parseDiffViewerQuery(searchParams);
    const filters = buildDiffFilters(query);
    const filtersKey = JSON.stringify(filters);

    const canAccessPage = UserHasAccess(requiredAction);
    const { blockedReason } = useRestrictedAccountIds({ enabled: canAccessPage });

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryFailed, setSummaryFailed] = useState(false);
    // The comparison the endpoint chose to show, as the summary last reported it. The page sends no mode of its own.
    const [comparisonMode, setComparisonMode] = useState(null);
    const [groups, setGroups] = useState(defaultArray);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [groupsFailed, setGroupsFailed] = useState(false);
    const [coverage, setCoverage] = useState(null);
    const [coverageFailed, setCoverageFailed] = useState(false);
    const [groupDifferences, setGroupDifferences] = useState(defaultArray);
    const [groupLoading, setGroupLoading] = useState(query.group != null);
    const [groupFailed, setGroupFailed] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [periodError, setPeriodError] = useState("");
    const [driverFilterText, setDriverFilterText] = useState(query.employee == null ? "" : `${query.employee}`);
    const [trendDays, setTrendDays] = useState(defaultArray);
    const [trendLoading, setTrendLoading] = useState(true);
    const [trendFailed, setTrendFailed] = useState(false);
    const [signOffDifference, setSignOffDifference] = useState(null);
    const [groupSignOff, setGroupSignOff] = useState(null);
    const [groupOpenCount, setGroupOpenCount] = useState(null);
    const [groupCountLoading, setGroupCountLoading] = useState(false);
    const [groupCountFailed, setGroupCountFailed] = useState(false);
    const [signOffSubmitting, setSignOffSubmitting] = useState(false);
    const [control, setControl] = useState(null);
    const [controlLoading, setControlLoading] = useState(true);
    const [controlFailed, setControlFailed] = useState(false);

    // A read can be re-issued while an earlier one is in flight, so only the most recent read of each kind
    // may write to the page.
    const latestSummaryRequest = useRef(0);
    const latestCoverageRequest = useRef(0);
    const latestGroupRequest = useRef(0);
    const latestTrendRequest = useRef(0);
    const latestGroupCountRequest = useRef(0);
    const latestControlRequest = useRef(0);
    const scrollRestored = useRef(false);
    const shownComparison = useRef(null);

    const debouncedDriverFilter = useDebounce(driverFilterText, driverDebounceTimeout);

    // With no period chosen the reads are sent no period and the picker stays empty.
    const periodFrom = query.from;
    const periodTo = query.to;
    const periodChosen = periodFrom != null && periodTo != null;
    const trendPeriod = buildTrendPeriod(periodFrom, periodTo);

    useEffect(() => {
        dispatch({ type: "SITE_FULL_WIDTH" });
    }, []);

    useEffect(loadSummaryAndGroups, [filtersKey, canAccessPage]);
    useEffect(loadCoverage, [query.from, query.to, comparisonMode, canAccessPage]);
    useEffect(loadGroupDifferences, [filtersKey, query.group, canAccessPage]);
    useEffect(loadTrend, [comparisonMode, trendPeriod.from, trendPeriod.to, trendPeriod.problem, query.provisional, canAccessPage]);
    useEffect(loadControl, [canAccessPage]);
    useEffect(applyDriverFilter, [debouncedDriverFilter]);
    useEffect(restoreScroll, [summaryLoading, groupsLoading, groupLoading]);
    useEffect(closeGroupOnComparisonChange, [comparisonMode]);

    function loadSummaryAndGroups() {
        if (!canAccessPage) {
            return;
        }

        const requestId = latestSummaryRequest.current + 1;
        latestSummaryRequest.current = requestId;

        setSummaryLoading(true);
        setSummaryFailed(false);
        setGroupsLoading(true);
        setGroupsFailed(false);

        const reads = RulesEngineService.getDiffViewerReads(filters);

        reads.summary.then((response) => {
            if (requestId === latestSummaryRequest.current) {
                const mappedSummary = mapDiffViewerSummary(response);

                setSummary(mappedSummary);
                setComparisonMode(mappedSummary.mode);
                setSummaryFailed(false);
            }
        }, (error) => {
            if (requestId === latestSummaryRequest.current) {
                setSummary(null);
                setSummaryFailed(true);
                showRequestError(error, differencesErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestSummaryRequest.current) {
                setSummaryLoading(false);
            }
        });

        reads.groups.then((response) => {
            if (requestId === latestSummaryRequest.current) {
                setGroups(mapGroupsResponse(response));
                setGroupsFailed(false);
            }
        }, (error) => {
            if (requestId === latestSummaryRequest.current) {
                setGroups(defaultArray);
                setGroupsFailed(true);
                showRequestError(error, groupsErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestSummaryRequest.current) {
                setGroupsLoading(false);
            }
        });
    }

    // The open group's rows are read again with the groups, so one retry recovers the whole list.
    function retryReads() {
        loadSummaryAndGroups();
        loadGroupDifferences();
    }

    function loadControl() {
        if (!canAccessPage) {
            return;
        }

        const requestId = latestControlRequest.current + 1;
        latestControlRequest.current = requestId;

        setControlLoading(true);
        setControlFailed(false);

        RulesEngineService.getUnderTestComparison().then((response) => {
            if (requestId === latestControlRequest.current) {
                setControl(mapControlResponse(response));
            }
        }, () => {
            if (requestId === latestControlRequest.current) {
                setControl(null);
                setControlFailed(true);
            }
        }).finally(() => {
            if (requestId === latestControlRequest.current) {
                setControlLoading(false);
            }
        });
    }

    // Coverage needs both dates, so it is read only once a period is chosen.
    function loadCoverage() {
        const requestId = latestCoverageRequest.current + 1;
        latestCoverageRequest.current = requestId;

        setCoverage(null);
        setCoverageFailed(false);

        if (!canAccessPage || !periodChosen) {
            return;
        }

        // Coverage measures only the legacy versus RE2 comparison, so it waits until that is known to be the one shown.
        if (comparisonMode !== comparisonModes.legacyVersusShadow) {
            return;
        }

        RulesEngineService.getInfringementDiffCoverage(query.from, query.to).then((response) => {
            if (requestId === latestCoverageRequest.current) {
                setCoverage(mapCoverageResponse(response));
                setCoverageFailed(false);
            }
        }, () => {
            if (requestId === latestCoverageRequest.current) {
                setCoverage(null);
                setCoverageFailed(true);
            }
        });
    }

    function loadGroupDifferences() {
        const requestId = latestGroupRequest.current + 1;
        latestGroupRequest.current = requestId;

        setGroupFailed(false);

        if (!canAccessPage || query.group == null) {
            setGroupDifferences(defaultArray);
            setGroupLoading(false);
            return;
        }

        setGroupLoading(true);

        RulesEngineService.getInfringementDiffs({ ...filters, signature: query.group }).then((response) => {
            if (requestId === latestGroupRequest.current) {
                setGroupDifferences(mapDifferencesResponse(response).differences);
                setGroupFailed(false);
            }
        }, (error) => {
            if (requestId === latestGroupRequest.current) {
                setGroupDifferences(defaultArray);
                setGroupFailed(true);
                showRequestError(error, differencesErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestGroupRequest.current) {
                setGroupLoading(false);
            }
        });
    }

    // The trend follows the comparison, the period and the "Show provisional" switch but no other filter. A
    // period it cannot cover is explained rather than read.
    function loadTrend() {
        if (!canAccessPage) {
            return;
        }

        const requestId = latestTrendRequest.current + 1;
        latestTrendRequest.current = requestId;

        setTrendFailed(false);

        if (trendPeriod.problem != null) {
            setTrendDays(defaultArray);
            setTrendLoading(false);
            return;
        }

        // The endpoint needs the comparison named, so the trend waits for the one the summary reports.
        if (comparisonMode == null) {
            return;
        }

        setTrendLoading(true);

        RulesEngineService.getInfringementDiffDailyCounts({
            mode: comparisonMode,
            from: trendPeriod.from,
            to: trendPeriod.to,
            includeProvisional: query.provisional
        }).then((response) => {
            if (requestId === latestTrendRequest.current) {
                setTrendDays(mapDailyCountsResponse(response).days);
                setTrendFailed(false);
            }
        }, (error) => {
            if (requestId === latestTrendRequest.current) {
                setTrendDays(defaultArray);
                setTrendFailed(true);
                showRequestError(error, trendErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestTrendRequest.current) {
                setTrendLoading(false);
            }
        });
    }

    // A sign-off changes what is open, and a change to the UnderTest comparison can change which comparison is shown,
    // so everything that counts differences is read again.
    function refreshDifferences() {
        loadSummaryAndGroups();
        loadGroupDifferences();
        loadTrend();
    }

    // With no comparison known the trend cannot be read, so its retry reads the summary that names one.
    function retryTrend() {
        if (comparisonMode == null) {
            retryReads();
            return;
        }

        loadTrend();
    }

    function onSignOff(difference) {
        setGroupSignOff(null);
        setSignOffDifference(difference);
    }

    // The open differences are counted with the filters, label and comparison the sign-off is sent with, so the
    // confirmation names how many it will cover; the sign-off is refused unless the group still holds that many.
    function onSignOffGroup(group) {
        const requestId = latestGroupCountRequest.current + 1;
        latestGroupCountRequest.current = requestId;
        const signOffFilters = { ...filters, mode: group.mode };

        setSignOffDifference(null);
        setGroupSignOff({ signature: group.signature, filters: signOffFilters });
        setGroupOpenCount(null);
        setGroupCountLoading(true);
        setGroupCountFailed(false);

        RulesEngineService.getInfringementDiffs({
            ...signOffFilters,
            signature: group.signature,
            includeSignedOff: false,
            includeWhitelisted: false
        }).then((response) => {
            if (requestId === latestGroupCountRequest.current) {
                setGroupOpenCount(mapDifferencesResponse(response).differences.length);
            }
        }, () => {
            if (requestId === latestGroupCountRequest.current) {
                setGroupCountFailed(true);
            }
        }).finally(() => {
            if (requestId === latestGroupCountRequest.current) {
                setGroupCountLoading(false);
            }
        });
    }

    function closeSignOff() {
        latestGroupCountRequest.current = latestGroupCountRequest.current + 1;
        setSignOffDifference(null);
        setGroupSignOff(null);
    }

    function onSignOffClose() {
        if (!signOffSubmitting) {
            closeSignOff();
        }
    }

    // A refusal, or any failure of a single sign-off, leaves the modal open with the judgement entered, so it can be
    // sent again or cancelled.
    function onSignOffSubmit(judgement) {
        setSignOffSubmitting(true);

        let request;

        if (groupSignOff != null) {
            const expectedCount = groupOpenCount;

            request = RulesEngineService.signOffInfringementDiffGroup(groupSignOff.filters, groupSignOff.signature, judgement, expectedCount).then((response) => {
                showGroupSignedOff(response?.signedOffCount ?? 0, expectedCount);
                closeSignOff();
                refreshDifferences();
            }, (error) => {
                showRequestError(error, groupSignOffErrorMessage);

                // Only a 400 is a refusal; after any other failure, a timeout included, the group may already be signed
                // off, so the confirmed count no longer holds and the confirmation is closed.
                if (error?.status !== badRequestCode) {
                    closeSignOff();
                    refreshDifferences();
                }
            });
        } else {
            request = RulesEngineService.signOffInfringementDiff(signOffDifference.id, judgement, signOffDifference.evidenceHash).then(() => {
                NotificationToaster.show(Intent.SUCCESS, signedOffMessage);
                closeSignOff();
                refreshDifferences();
            }, (error) => {
                showRequestError(error, signOffErrorMessage);
            });
        }

        request.finally(() => {
            setSignOffSubmitting(false);
        });
    }

    // Coming back from the harness to the view it was opened from puts the operator back where they
    // were on the page, once, after the rows it was scrolled over are on screen again.
    function restoreScroll() {
        if (scrollRestored.current || summaryLoading || groupsLoading || groupLoading) {
            return;
        }

        scrollRestored.current = true;

        if (diffViewerReturn == null || diffViewerReturn.search !== location.search) {
            return;
        }

        window.scrollTo(0, diffViewerReturn.scrollY);
    }

    function updateQuery(change) {
        const nextQuery = { ...query, page: 1, ...change };

        setSearchParams(buildDiffViewerSearchParams(nextQuery), { replace: true });
    }

    // A group's signature belongs to the comparison it was opened in, so the open group and its page close when the
    // comparison shown changes. The first comparison reported keeps them, so a link to an open group still opens it.
    function closeGroupOnComparisonChange() {
        const previous = shownComparison.current;
        shownComparison.current = comparisonMode;

        if (previous == null || comparisonMode == null || previous === comparisonMode) {
            return;
        }

        if (query.group != null || query.page > 1) {
            updateQuery({ group: null });
        }
    }

    function applyDriverFilter() {
        const trimmed = debouncedDriverFilter.trim();
        let employee = null;

        if (trimmed !== "") {
            if (!positiveIntegerPattern.test(trimmed)) {
                return;
            }

            employee = Number(trimmed);
        }

        if (employee !== query.employee) {
            updateQuery({ employee: employee });
        }
    }

    function onDriverFilterChange(event) {
        setDriverFilterText(event.target.value);
    }

    function onPeriodChange(range) {
        const from = range.startDate.dateString;
        const to = range.endDate.dateString;

        if (from == null && to == null) {
            setPeriodError("");
            updateQuery({ from: null, to: null });
            return;
        }

        if (from == null || to == null) {
            return;
        }

        if (!isDateRangeValid(from, to)) {
            setPeriodError(invalidRangeMessage);
            return;
        }

        setPeriodError("");
        updateQuery({ from: from, to: to });
    }

    function onTypeSelect(item) {
        updateQuery({ type: item.id === allOption ? null : Number(item.id) });
    }

    // A sign-off label only matches signed-off differences, so choosing one shows them too.
    function onLabelSelect(item) {
        if (item.id === allOption) {
            updateQuery({ label: null });
            return;
        }

        if (signOffLabels.includes(item.id)) {
            updateQuery({ label: item.id, signedOff: true });
            return;
        }

        updateQuery({ label: item.id });
    }

    function onProvisionalToggle(event) {
        updateQuery({ provisional: event.target.checked });
    }

    function onWhitelistedToggle(event) {
        updateQuery({ whitelisted: event.target.checked });
    }

    function onSignedOffToggle(event) {
        updateQuery({ signedOff: event.target.checked });
    }

    function onToggleGroup(signature) {
        updateQuery({ group: signature === query.group ? null : signature });
    }

    function onPageChange(newPage) {
        updateQuery({ page: newPage });
    }

    // The harness's way back to the viewer returns to the view being left, not an older one.
    function rememberView() {
        dispatch(SetRulesEngineDiffViewerReturn({ search: location.search, scrollY: window.scrollY }));
    }

    function onBackToHarness() {
        rememberView();
        navigate(harnessRoute);
    }

    function onOpenInHarness(difference) {
        dispatch(SetRulesEngineDriver(difference.driver));
        dispatch(SetRulesEngineRange(buildHarnessRange(difference)));
        rememberView();
        navigate(harnessRoute);
    }

    // Both downloads cover every difference the filters admit, so they read again without the open group.
    function download(save) {
        setDownloading(true);

        RulesEngineService.getInfringementDiffs(filters).then((response) => {
            return save(response);
        }, (error) => {
            showRequestError(error, differencesErrorMessage);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
        }).finally(() => {
            setDownloading(false);
        });
    }

    // Named for the period and comparison the read reports, else the ones on screen.
    function buildFileNameModel(differencesModel) {
        const period = [
            { from: differencesModel.from, to: differencesModel.to },
            { from: periodFrom, to: periodTo }
        ].find((candidate) => candidate.from != null && candidate.to != null);

        return {
            mode: differencesModel.mode ?? comparisonMode,
            from: period?.from ?? null,
            to: period?.to ?? null
        };
    }

    // The Excel file keeps the driver's personal details for the people who read it, so it is flagged as sensitive.
    function onDownloadExcel() {
        download((response) => {
            const differencesModel = mapDifferencesResponse(response);

            return buildXlsxWorkbook(buildDiffViewerSheets(differencesModel.differences, groups)).then((workbook) => {
                HelperFunctions.downloadFile(workbook, buildDiffViewerFileName(buildFileNameModel(differencesModel), "xlsx", new Date()));
                NotificationToaster.show(Intent.WARNING, excelWarningMessage);
            });
        });
    }

    function onDownloadJson() {
        download((response) => {
            const differencesModel = mapDifferencesResponse(response);
            const json = new Blob([buildDiffViewerJson(response, RulesEngineService.buildInfringementDiffQuery(filters))], { type: "application/json" });

            HelperFunctions.downloadFile(json, buildDiffViewerFileName(buildFileNameModel(differencesModel), "json", new Date()));
        });
    }

    // A type is named by its code, since one type spans many sub codes; two types sharing a code are told
    // apart by their ids.
    function buildTypeItems() {
        const typeOptions = summary?.typeOptions ?? defaultArray;
        const sharedCodes = typeOptions
            .map((option) => option.code)
            .filter((code, index, codes) => codes.indexOf(code) !== index);

        function buildTypeName(option) {
            if (sharedCodes.includes(option.code)) {
                return `${formatValue(option.code)} (type ${option.id})`;
            }

            return formatValue(option.code);
        }

        const items = [
            { id: allOption, name: "All infringement types" },
            ...typeOptions.map((option) => ({ id: `${option.id}`, name: buildTypeName(option) }))
        ];

        if (query.type != null && !typeOptions.some((option) => option.id === query.type)) {
            items.push({ id: `${query.type}`, name: `Type ${query.type}` });
        }

        return items;
    }

    // The summary failed before naming a comparison, so nothing that depends on one can be read.
    const comparisonUnknown = summaryFailed && comparisonMode == null;
    const whitelistedCount = summary?.whitelistedCount ?? 0;
    const showWhitelistedHidden = !query.whitelisted && summary != null && !summaryLoading && whitelistedCount > 0;
    let driverFilterError = "";

    if (driverFilterText.trim() !== "" && !positiveIntegerPattern.test(driverFilterText.trim())) {
        driverFilterError = invalidDriverMessage;
    }

    let signOffTitle = "Sign off difference";

    if (groupSignOff != null) {
        signOffTitle = "Sign off group";
    } else if (signOffDifference?.signOff != null) {
        signOffTitle = "Change sign-off";
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <Breadcrumbs items={breadcrumbs} />
                        <h1>Rules Engine Diff Viewer</h1>

                        <ShowHide
                            evaluator={!summaryFailed}
                            show={(
                                <div className={classNames("intro-text", { "bp6-skeleton": comparisonMode == null })}>
                                    <p id="rules-engine-diff-intro">{introTexts[comparisonMode] ?? introPlaceholder}</p>
                                    <p id="rules-engine-diff-comparison">{`Comparison: ${formatMode(comparisonMode)}`}</p>
                                </div>
                            )}
                        />

                        <div className="spacer-bottom-medium inline-items">
                            <Button
                                id="rules-engine-diff-back"
                                text="Back to harness"
                                icon="arrow-left"
                                onClick={onBackToHarness}
                            />
                            <Button
                                id="rules-engine-diff-download"
                                text="Download Excel"
                                icon="download"
                                loading={downloading}
                                disabled={downloading}
                                onClick={onDownloadExcel}
                            />
                            <Button
                                id="rules-engine-diff-download-json"
                                text="Download JSON"
                                icon="code"
                                loading={downloading}
                                disabled={downloading}
                                onClick={onDownloadJson}
                            />
                        </div>

                        <NotificationInline
                            intent="warning"
                            show
                            allowClose={false}
                            text={utcNotice}
                        />

                        <DiffViewerComparisonControl
                            control={control}
                            loading={controlLoading}
                            failed={controlFailed}
                            onRefresh={loadControl}
                            onChanged={refreshDifferences}
                        />

                        <DiffViewerGatePanel
                            gate={summary?.gate ?? null}
                            gateFailed={summaryFailed}
                            coverage={coverage}
                            coverageFailed={coverageFailed || comparisonUnknown}
                            coverageMeasured={comparisonMode !== comparisonModes.underTest}
                            periodChosen={periodChosen}
                            loading={summaryLoading && summary == null}
                        />

                        <div className="spacer-bottom-small rules-engine-diff-filters" id="rules-engine-diff-filters">
                            <FormDateRangeInput
                                id="rules-engine-diff-period"
                                headingText="Period"
                                startDate={periodFrom}
                                endDate={periodTo}
                                maxDate={moment().toDate()}
                                dangerHelperText={periodError}
                                onChange={onPeriodChange}
                            />
                            <FormSelect
                                id="rules-engine-diff-type"
                                headingText="Infringement type"
                                items={buildTypeItems()}
                                selectedValue={query.type == null ? allOption : `${query.type}`}
                                onItemSelect={onTypeSelect}
                            />
                            <FormSelect
                                id="rules-engine-diff-label"
                                headingText="Label"
                                items={labelItems}
                                selectedValue={query.label ?? allOption}
                                onItemSelect={onLabelSelect}
                            />
                            <FormTextInput
                                id="rules-engine-diff-driver"
                                headingText="Driver"
                                placeholder="Employee id"
                                value={driverFilterText}
                                dangerHelperText={driverFilterError}
                                fill
                                onChange={onDriverFilterChange}
                            />
                        </div>

                        <div className="spacer-bottom rules-engine-diff-toggles" id="rules-engine-diff-toggles">
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-provisional"
                                    label="Show provisional"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.provisional}
                                    onChange={onProvisionalToggle}
                                />
                            </div>
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-whitelisted"
                                    label="Show whitelisted"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.whitelisted}
                                    onChange={onWhitelistedToggle}
                                />
                            </div>
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-signed-off"
                                    label="Show signed off"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.signedOff}
                                    onChange={onSignedOffToggle}
                                />
                            </div>
                        </div>

                        <DiffViewerTrend
                            days={trendDays}
                            loading={trendLoading && !comparisonUnknown}
                            failed={trendFailed || comparisonUnknown}
                            period={trendPeriod.problem == null ? trendPeriod : null}
                            missingDriversText={comparisonMode === comparisonModes.underTest ? notMeasuredText : notAvailableText}
                            unavailableMessage={trendUnavailableMessages[trendPeriod.problem] ?? null}
                            onRetry={retryTrend}
                        />

                        <ShowHide
                            evaluator={showWhitelistedHidden}
                            show={(
                                <p id="rules-engine-diff-whitelisted-hidden" className="description-text">
                                    {`${whitelistedCount} whitelisted ${whitelistedCount === 1 ? "difference" : "differences"} hidden`}
                                </p>
                            )}
                        />

                        <DiffViewerGroups
                            groups={groups}
                            openSignature={query.group}
                            loading={groupsLoading}
                            failed={groupsFailed}
                            onToggleGroup={onToggleGroup}
                            onRetry={retryReads}
                            onSignOffGroup={onSignOffGroup}
                        >
                            <DiffViewerDifferences
                                differences={groupDifferences}
                                page={query.page}
                                loading={groupLoading}
                                failed={groupFailed}
                                onRetry={loadGroupDifferences}
                                blockedReason={blockedReason}
                                onPageChange={onPageChange}
                                onOpenInHarness={onOpenInHarness}
                                onSignOff={onSignOff}
                            />
                        </DiffViewerGroups>

                        <DiffViewerSignOffModal
                            isOpen={signOffDifference != null || groupSignOff != null}
                            title={signOffTitle}
                            initialJudgement={signOffDifference?.signOff ?? null}
                            group={groupSignOff != null}
                            openCount={groupOpenCount}
                            countLoading={groupCountLoading}
                            countFailed={groupCountFailed}
                            submitting={signOffSubmitting}
                            onSubmit={onSignOffSubmit}
                            onClose={onSignOffClose}
                        />
                    </div>
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

RulesEngineDiffViewer.propTypes = {
    breadcrumbs: PropTypes.array
};
